import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Colors from "./Colors";

export const getTimeDiff = (createdAt: string): string => {
  const createdAtDate = new Date(createdAt);
  const now = new Date();

  const diffInMilliseconds = now.getTime() - createdAtDate.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hr`;
  } else {
    return `${diffInDays} day`;
  }
};

export const convertToDecimal = (num: number) => {
  return Math.ceil(num * 100) / 100;
};

export const convertToRupiah = (num: any) => {
  if (num) {
    return `Rp ${num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  }
};

export const saveToLocalStorage = (key: any, value: any) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const getFromLocalStorage = (key: any) => {
  const storedValue = localStorage.getItem(key);
  return storedValue ? JSON.parse(storedValue) : null;
};

export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  return isOnline;
};

export const removeSession = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userId");
  localStorage.removeItem("Name");
  localStorage.removeItem("name");
  localStorage.removeItem("Username");
  localStorage.removeItem("username");
  localStorage.removeItem("IsLogin");
  localStorage.removeItem("isLogin");
  localStorage.removeItem("platform");
  localStorage.removeItem("deviceID");
  localStorage.removeItem("DikantorAddtoHome");
  localStorage.removeItem("remember");
  localStorage.removeItem("splide_list");
  localStorage.removeItem("list_menu");
};

export const getLocation = () => {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });
};

export const getCityName = async (latitude: any, longitude: any) => {
  const response = await axios.get(
    `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
  );
  const data = response.data;
  return data.city;
};

export const getWeather = async (city: any) => {
  const apiKey = "ada4b63b63248fe74f65f37617b5d8b1";
  const response = await axios.get(
    `https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${apiKey}&units=metric`,
  );
  const data = response.data;
  return data;
};

export const getTodayDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ][today.getMonth()];
  const year = today.getFullYear();
  return `${day} ${month} ${year}`;
}; //Output: "1 January 2023"

export const checkLocationPermission = async () => {
  try {
    const permissionStatus = await navigator.permissions.query({
      name: "geolocation",
    });

    if (permissionStatus.state === "granted") {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
    } else if (permissionStatus.state === "prompt") {
      // The user has not yet granted or denied permission
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });
    } else {
      // Permission is denied
      throw new Error("Location permission denied");
    }
  } catch (error) {
    toast.error(
      "Location services are not enabled. Please enable location services to proceed.",
    );
  }
};

export function getCurrentMonth(date: any) {
  const now = date;
  const monthName = now.toLocaleString("default", { month: "long" });
  const year = now.getFullYear();
  return `${monthName} ${year}`;
}

export function formatTimePresence(time: number) {
  if (time) {
    const formattedTime = dayjs(time).format("HH:mm");
    return formattedTime; //output "10:30"
  }
}

export function formatTime(time: number) {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`; //output "10:30"
}

export function formatTimePreference(time: string) {
  if (time) {
    const [hour, minute] = time.split(":").map(Number);
    const formattedHour = hour.toString().padStart(2, "0");
    const formattedMinute = minute.toString().padStart(2, "0");
    return `${formattedHour}:${formattedMinute}`; //output "07:30" jika inputnya "7:30"
  }
}

export function getCurrentDate() {
  const currentDate = new Date();

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day = days[currentDate.getDay()];
  const date = currentDate.getDate();
  const month = months[currentDate.getMonth()];
  const year = currentDate.getFullYear();

  const formattedDate = `${day}, ${date} ${month} ${year}`;

  return formattedDate; //Output: "Monday, 1 January 2023"
}

export function dateFormatterWithDay(dateString: string) {
  if (dateString) {
    const date = new Date(dateString);
    const day = date.toLocaleString("en-US", { weekday: "long" });
    const dayOfMonth = date.toLocaleString("en-US", { day: "2-digit" });
    const month = date.toLocaleString("en-US", { month: "long" });
    const year = date.toLocaleString("en-US", { year: "numeric" });

    return `${day}, ${dayOfMonth} ${month} ${year}`; //Output: "Monday, 1 January 2023"
  }
}

export function dateFormatter(dateString: string) {
  if (dateString) {
    const months: string[] = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];

    const [year, month, day] = dateString.split("-");
    const monthIndex = parseInt(month) - 1;
    const formattedDate = `${parseInt(day)} ${months[monthIndex]} ${year}`;

    return formattedDate; //Output: "1 January 2023"
  }
}

export function cutName(fullName: any) {
  if (fullName) {
    const nameArray = fullName.split(" ");
    const cutName = nameArray.slice(0, 2).join(" ");
    return cutName; //Output: "John Doe"
  }
}

export function sortArray(array: number[]) {
  return array.sort((a, b) => a - b); //Output: [1, 2, 3, 4, 5]
}

export function truncateString(str: string, num: number) {
  return str.length > num ? str.slice(0, num > 3 ? num - 3 : num) + "..." : str; //Output: "Hello..."
}

export function reverseString(str: string): string {
  return str.split("").reverse().join(""); //Output: "olleh"
}

export function truncateEmail(email: string) {
  const atIndex = email.indexOf("@");
  const name = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  const firstThreeChars = name.slice(0, 3);
  const lastThreeChars = name.length > 3 ? name.slice(-3) : "";
  const middleMask = "*".repeat(Math.max(1, name.length - 6));

  return `${firstThreeChars}${middleMask}${lastThreeChars}${domain}`;
}

export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      (error) => reject(error),
    );
  }); //Output: {latitude: 37.7749, longitude: -122.4194}
}

export function capitalizeFirstLetter(str: string) {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1); //Output: "Hello"
  }
}

export function base64toBlob(base64Data: string, contentType = "") {
  // Remove the data URL prefix if it exists
  const base64WithoutPrefix = base64Data.replace(/^data:[^;]+;base64,/, "");

  // Convert the base64 string to a Uint8Array
  const byteCharacters = atob(base64WithoutPrefix);
  const byteArray = new Uint8Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArray[i] = byteCharacters.charCodeAt(i) & 0xff;
  }

  // Create the Blob object
  return new Blob([byteArray.buffer], { type: contentType });
}

export function currencyFormatter(number: number) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number); //Output: "Rp10.000"
}

export const isEmoji = (str: string) => {
  const regex =
    /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
  return regex.test(str);
};

export const generateTimes = (interval = 15) => {
  const times = [];
  const today = dayjs().format("YYYY-MM-DD");
  const tomorrow = dayjs().add(1, "day").format("YYYY-MM-DD");

  for (let i = 0; i < 24 * 60; i += interval) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    times.push({ time, date: today });
  }

  for (let i = 0; i < 24 * 60; i += interval) {
    const hours = Math.floor(i / 60);
    const minutes = i % 60;
    const time = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    times.push({ time, date: tomorrow });
  }

  return times;
}; // Output: [{time: "00:00", date: "2023-01-01"}, {time: "00:15", date: "2023-01-01"}, ...]

export function statusColorTag(status: string) {
  switch (status) {
    case "Outstanding Release":
      return { color: Colors.primary, backgroundColor: Colors.primary + "10" };
    case "Unbilled":
      return { color: "#FFA117", backgroundColor: "#FFF6E8" };
    case "Reconsilled":
      return { color: "#FFC700", backgroundColor: "#FFF8DD" };
    case "BA Receipt":
      return { color: "#4FC3F7", backgroundColor: "#EEF9FF" };
    case "Ready to Billed":
      return { color: "#5ED300", backgroundColor: "#DFF6CC" };
    case "Billed":
      return { color: "#BA68C8", backgroundColor: "#F9F0FA" };
    default:
      return { color: Colors.primary, backgroundColor: Colors.primary + "10" };
  }
}

export function statusText(status: string) {
  switch (status) {
    case "Submitted":
      return "Outstanding Release";
    case "Released":
      return "Unbilled";
    case "Reconsilled":
      return "Reconsilled";
    case "BA Receipt":
      return "BA Receipt";
    case "Ready to Billed":
      return "Ready to Billed";
    case "Billed":
      return "Billed";
    default:
      return status;
  }
}

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const sanitizeDownloadFileName = (value: string, fallback: string): string => {
  const sanitized = value
    .replace(/^['"]|['"]$/g, "")
    .replace(/[\\/]+/g, "-")
    .trim();

  return sanitized || fallback;
};

export function parseFileNameFromContentDisposition(
  contentDisposition: string | null,
  fallbackFileName?: string,
): string {
  const fallback = fallbackFileName?.trim() || "download";

  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i,
  );

  if (utf8Match?.[1]) {
    return sanitizeDownloadFileName(
      safeDecodeURIComponent(utf8Match[1].trim()),
      fallback,
    );
  }

  const plainMatch = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i,
  );
  const rawFileName = plainMatch?.[1] ?? plainMatch?.[2];

  if (!rawFileName) {
    return fallback;
  }

  return sanitizeDownloadFileName(rawFileName.trim(), fallback);
}

export function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";

  document.body.appendChild(link);

  try {
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}

const MIME_EXTENSION_MAP: Record<string, string> = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "text/csv": "csv",
  "application/zip": "zip",
  "application/json": "json",
  "text/plain": "txt",
};

export function ensureDownloadFileExtension(
  fileName: string,
  blob: Blob,
): string {
  const normalized = fileName.trim() || "download";

  if (/\.[a-z0-9]{1,10}$/i.test(normalized)) {
    return normalized;
  }

  const mimeType = blob.type?.split(";")[0]?.trim().toLowerCase();
  const extension = mimeType ? MIME_EXTENSION_MAP[mimeType] : undefined;

  return extension ? `${normalized}.${extension}` : normalized;
}
